import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

// ============================================================================
// Support ticketek — DTO-k a 23-logic.js supTasks/supLabels/supportVals
// state-jéből levezetve.
// ============================================================================

/** A ticket státusza = a kanban oszlop neve — az oszlopok szabadon bővíthetők. */
export type SupportTicketStatus = string;

/** Kanban oszlop (tábla) a Support oldalon. */
export interface SupportColumn {
  id: number;
  name: string;
  color: string;
  position: number;
  /** „kész" oszlop: nincs élő határidő, nincs értesítés */
  isDone: boolean;
  ticketCount: number;
}
export type SupportTicketPriority = 'P1' | 'P2' | 'P3';

export interface SupportLabel {
  id: number;
  name: string;
  /** hex szín, pl. '#5E7FA8' */
  color: string;
}

export interface CreateSupportLabelRequest {
  name: string;
  color: string;
}

export interface SupportComment {
  id: number;
  /** a szerző user id-ja — csak ő törölheti a saját hozzászólását */
  authorUserId: number | null;
  /** kommentelő user neve */
  author: string;
  text: string;
  /** ISO dátum */
  createdAt: string;
}

export interface SupportTicketOrderRef {
  /** belső rendelés id (orders/:id) */
  orderId: number;
  /** megjelenített rendelésszám, pl. '#4108634493' */
  displayId: string;
  customerName: string;
  shop: string;
}

export interface SupportPerson {
  id: number;
  name: string;
}

/** Saját emlékeztető (Support oldal teteje): határidő előtt 1 órával e-mail a felelősöknek. */
export interface SupportReminder {
  id: number;
  title: string;
  description: string;
  /** ISO dátum vagy null (nincs határidő) */
  dueAt: string | null;
  creator: SupportPerson | null;
  createdAt: string;
  doneAt: string | null;
  notifiedAt: string | null;
  assignees: SupportPerson[];
}

export interface SaveSupportReminderRequest {
  title?: string;
  description?: string | null;
  dueAt?: string | null;
  assigneeUserIds?: number[];
  done?: boolean;
}

/** A taskhoz kapcsolt e-mail beszélgetés (Support → Levelezés). */
export interface SupportTicketMailThreadRef {
  id: number;
  /** pl. 'L-45' */
  displayId: string;
  subject: string;
  customerName: string;
  customerEmail: string;
  status: string;
  unread: boolean;
  lastMessageAt: string;
}

export interface SupportTicket {
  id: number;
  /** megjelenített azonosító, pl. 'T-123' */
  displayId: string;
  title: string;
  description: string;
  status: SupportTicketStatus;
  priority: SupportTicketPriority;
  /** létrehozó user neve */
  creator: string;
  /** ISO dátum — az SLA határidő ebből számolódik (P1: 24ó, P2: 3 munkanap, P3: 5 munkanap) */
  createdAt: string;
  /** 'Etsy' | 'Jump' | 'Shopify' | 'BGFox' | null */
  shop: string | null;
  order: SupportTicketOrderRef | null;
  labelIds: number[];
  comments: SupportComment[];
  /** csatolt kép fájlnevek (support/images/:file) */
  attachments: string[];
  /** felelősök — ők kapják a határidő előtti értesítést */
  assignees: SupportPerson[];
  /** „kész" oszlopban van (nincs élő határidő) */
  isDone: boolean;
  /** kapcsolt e-mail beszélgetések */
  mailThreads: SupportTicketMailThreadRef[];
  /** még nem látott hozzászólások (mások írták) — zöld jelzés a kártyán */
  unreadComments: number;
  unreadCommentAuthors: string[];
}

export interface CreateSupportTicketRequest {
  title: string;
  description?: string;
  status?: SupportTicketStatus;
  priority?: SupportTicketPriority;
  shop?: string | null;
  orderId?: number | null;
  labelIds?: number[];
  assigneeUserIds?: number[];
  /** kapcsolt e-mail beszélgetések (L-azonosítók számai) */
  mailThreadIds?: number[];
}

export type UpdateSupportTicketRequest = Partial<CreateSupportTicketRequest>;

/** A rendeléshez kötött task / levelezés (a rendelés oldal jobb oszlopa). */
export interface OrderSupportTicketLink {
  id: number;
  displayId: string;
  title: string;
  status: string;
  statusColor: string | null;
  done: boolean;
  priority: string;
  assignees: string[];
  createdAt: string;
  updatedAt: string | null;
  /** közvetett kapcsolat: melyik levelezésen át (L-5) */
  via: string | null;
}

export interface OrderSupportThreadLink {
  id: number;
  displayId: string;
  subject: string;
  customer: string;
  status: string;
  unread: boolean;
  messageCount: number;
  lastMessageAt: string | null;
  lastDirection: 'in' | 'out';
  /** közvetett kapcsolat: melyik taskon át (T-12) */
  via: string | null;
}

export interface OrderSupportLinks {
  tickets: OrderSupportTicketLink[];
  threads: OrderSupportThreadLink[];
}

@Injectable({ providedIn: 'root' })
export class SupportService {

  constructor(private http: HttpClient) {}

  /** GET support/columns — a tábla oszlopai sorrendben */
  getColumns(): Observable<SupportColumn[]> {
    return this.http.get<SupportColumn[]>('support/columns');
  }

  createColumn(request: { name: string; color?: string; isDone?: boolean }): Observable<SupportColumn> {
    return this.http.post<SupportColumn>('support/columns', request);
  }

  updateColumn(id: number, request: { name?: string; color?: string; position?: number; isDone?: boolean }): Observable<SupportColumn> {
    return this.http.patch<SupportColumn>(`support/columns/${id}`, request);
  }

  /** PUT support/columns/order — az oszlopok új sorrendje (id-lista) */
  reorderColumns(ids: number[]): Observable<SupportColumn[]> {
    return this.http.put<SupportColumn[]>('support/columns/order', { ids });
  }

  /** DELETE support/columns/:id?moveTo= — ha vannak taskok, a cél oszlop kötelező */
  deleteColumn(id: number, moveTo?: number | null): Observable<{ deleted: true; moved: number }> {
    const suffix = moveTo ? `?moveTo=${moveTo}` : '';
    return this.http.delete<{ deleted: true; moved: number }>(`support/columns/${id}${suffix}`);
  }

  /** GET support/reminders — saját emlékeztetők (létrehozó vagy felelős) */
  getReminders(): Observable<SupportReminder[]> {
    return this.http.get<SupportReminder[]>('support/reminders');
  }

  createReminder(request: SaveSupportReminderRequest): Observable<SupportReminder> {
    return this.http.post<SupportReminder>('support/reminders', request);
  }

  updateReminder(id: number, request: SaveSupportReminderRequest): Observable<SupportReminder> {
    return this.http.patch<SupportReminder>(`support/reminders/${id}`, request);
  }

  deleteReminder(id: number): Observable<void> {
    return this.http.delete<void>(`support/reminders/${id}`);
  }

  /** GET support/tickets — minden ticket (a kanban kliens oldalon oszlopoz) */
  /** A rendeléshez kötött taskok és levelezések (jogosultság szerint szűrve). */
  getOrderLinks(orderId: number): Observable<OrderSupportLinks> {
    return this.http.get<OrderSupportLinks>(`support/orders/${orderId}/links`);
  }

  getTickets(): Observable<SupportTicket[]> {
    return this.http.get<SupportTicket[]>('support/tickets');
  }

  /** POST support/tickets */
  createTicket(request: CreateSupportTicketRequest): Observable<SupportTicket> {
    return this.http.post<SupportTicket>('support/tickets', request);
  }

  /** PATCH support/tickets/:id — státusz drag&drop is ezt hívja */
  updateTicket(id: number, request: UpdateSupportTicketRequest): Observable<SupportTicket> {
    return this.http.patch<SupportTicket>(`support/tickets/${id}`, request);
  }

  /** POST support/tickets/:id/seen — a beszélgetést megnézte, a jelzés törlődik */
  markTicketSeen(ticketId: number): Observable<{ ok: true }> {
    return this.http.post<{ ok: true }>(`support/tickets/${ticketId}/seen`, {});
  }

  /** DELETE support/tickets/:id */
  deleteTicket(id: number): Observable<void> {
    return this.http.delete<void>(`support/tickets/${id}`);
  }

  /** POST support/tickets/:id/comments */
  addComment(ticketId: number, text: string): Observable<SupportComment> {
    return this.http.post<SupportComment>(`support/tickets/${ticketId}/comments`, { text });
  }

  /** DELETE support/tickets/:id/comments/:commentId — csak a szerző törölheti */
  deleteComment(ticketId: number, commentId: number): Observable<{ deleted: true }> {
    return this.http.delete<{ deleted: true }>(`support/tickets/${ticketId}/comments/${commentId}`);
  }

  /** POST support/tickets/:id/attachments (multipart, 'image') */
  addAttachment(ticketId: number, file: File): Observable<{ attachments: string[] }> {
    const fd = new FormData();
    fd.append('image', file);
    return this.http.post<{ attachments: string[] }>(`support/tickets/${ticketId}/attachments`, fd);
  }

  /** DELETE support/tickets/:id/attachments/:fileName */
  removeAttachment(ticketId: number, fileName: string): Observable<{ attachments: string[] }> {
    return this.http.delete<{ attachments: string[] }>(`support/tickets/${ticketId}/attachments/${fileName}`);
  }

  /** GET support/labels */
  getLabels(): Observable<SupportLabel[]> {
    return this.http.get<SupportLabel[]>('support/labels');
  }

  /** POST support/labels */
  createLabel(request: CreateSupportLabelRequest): Observable<SupportLabel> {
    return this.http.post<SupportLabel>('support/labels', request);
  }

  /** DELETE support/labels/:id — a címke minden ticketről lekerül, a ticketek megmaradnak */
  deleteLabel(id: number): Observable<void> {
    return this.http.delete<void>(`support/labels/${id}`);
  }
}
